/**
 * PauseOverlay - shown when the game is paused (all human players have paused)
 * Also shows resume countdown when game is resuming
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useShallow } from 'zustand/react/shallow';
import { useGameStore } from '../../stores/gameStore';
import { socketActions } from '../../lib/socketActions';
import { isClaimInput } from '../../hooks/useKeyboardShortcuts';

const togglePause = () => socketActions.togglePause();

export function PauseOverlay() {
  const playerId = useGameStore((s) => s.playerId);
  const resumeCountdownEndsAt = useGameStore((s) => s.resumeCountdownEndsAt);
  const isGamePaused = useGameStore((s) => s.gameState?.isPaused ?? false);
  const isInGame = useGameStore((s) => s.gameState?.status === 'playing');
  const isPaused = useGameStore((s) => s.gameState?.pausedPlayerIds?.includes(playerId || '') ?? false);
  // Names of human players who have paused
  const pausedPlayerNames = useGameStore(
    useShallow((s) =>
      s.gameState?.players
        .filter((p) => !p.isAI && s.gameState?.pausedPlayerIds?.includes(p.id))
        .map((p) => p.name) ?? []
    )
  );

  // Countdown state
  const [countdown, setCountdown] = useState<number | null>(null);

  // Handle Esc key for pause toggle (only if no steal selections)
  useEffect(() => {
    if (!isInGame) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Already handled (e.g. Settings/Chat closed itself)
        if (e.defaultPrevented) return;
        const target = e.target as HTMLElement | null;
        // Esc inside another dialog belongs to that dialog
        if (target?.closest?.('[role="dialog"]')) return;
        // Esc while typing in a secondary field (chat etc.) shouldn't pause;
        // the claim input is always focused during play, so it still pauses
        const isTextField =
          !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable);
        if (isTextField && !isClaimInput(target)) return;

        // If words are selected for stealing, clear them instead of pausing
        const { selectedWordsToSteal, clearWordsToSteal } = useGameStore.getState();
        if (selectedWordsToSteal.size > 0) {
          e.preventDefault();
          clearWordsToSteal();
          return;
        }

        e.preventDefault();
        togglePause();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isInGame]);

  // Update countdown every 100ms when resuming
  useEffect(() => {
    if (!resumeCountdownEndsAt) {
      setCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const remaining = Math.max(0, resumeCountdownEndsAt - Date.now());
      const seconds = Math.ceil(remaining / 1000);

      if (remaining <= 0) {
        setCountdown(null);
        useGameStore.getState().setResumeCountdownEndsAt(null);
      } else {
        setCountdown(seconds);
      }
    };

    // Initial update
    updateCountdown();

    // Update every 100ms for smoother countdown
    const interval = setInterval(updateCountdown, 100);

    return () => clearInterval(interval);
  }, [resumeCountdownEndsAt]);

  // Show countdown overlay when resuming
  const showCountdown = countdown !== null && countdown > 0;

  // Show pause overlay when game is paused OR counting down
  const showOverlay = (isGamePaused && isInGame) || showCountdown;

  return (
    <AnimatePresence>
      {showOverlay && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 sm:p-8 landscape:max-h-[calc(100dvh-1rem)] landscape:overflow-y-auto max-w-md mx-4 text-center"
            role="alertdialog"
            aria-modal="true"
            aria-label={showCountdown ? 'Resuming game' : 'Game paused'}
          >
            {showCountdown ? (
              // Resume countdown
              <>
                <motion.div
                  key={countdown}
                  initial={{ scale: 1.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-8xl font-bold text-green-600 dark:text-green-400 mb-4"
                >
                  {countdown}
                </motion.div>

                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  Resuming...
                </h2>

                <p className="text-slate-600 dark:text-slate-400">
                  Get ready!
                </p>
              </>
            ) : (
              // Paused state
              <>
                {/* Pause icon */}
                <div className="text-6xl mb-4">||</div>

                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  Game Paused
                </h2>

                <p className="text-slate-600 dark:text-slate-400 mb-6">
                  {pausedPlayerNames.length === 1
                    ? `${pausedPlayerNames[0]} has paused the game.`
                    : pausedPlayerNames.length > 1
                    ? `${pausedPlayerNames.join(', ')} have paused the game.`
                    : 'The game is paused.'}
                </p>

                <p className="text-sm text-slate-500 dark:text-slate-500 mb-6">
                  Game will resume when any player unpauses.
                </p>

                <button
                  onClick={togglePause}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors shadow-lg"
                >
                  {isPaused ? 'Resume Game' : 'Unpause'}
                </button>

                <p className="text-xs text-slate-400 dark:text-slate-500 mt-4">
                  Press <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 rounded text-xs font-mono">Esc</kbd> to toggle pause
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
